import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import {
  getDailyBreakdownCache,
  setDailyBreakdownCache,
} from "../lib/setterPerformanceCacheService";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";

// =============================================================================
// USE SETTER DAILY BREAKDOWN HOOK
// =============================================================================
// PURPOSE: Lazy-fetch per-day work breakdown for a single setter.
// Called when user clicks "View Days" in the Setter Scorecard modal.
//
// DATA SOURCE: get_setter_daily_breakdown RPC
//
// FEATURES:
// - Lazy fetch (only fetches when fetch() is called, not on mount)
// - Stale-while-revalidate caching (5 min TTL)
// - AbortController cleanup on unmount
// - Filters to requested setter only (RPC returns all setters)
// - Sorts by work_date DESC (most recent first)
// =============================================================================

export interface DailyBreakdownRow {
  setter_id: string;
  setter_name: string;
  work_date: string; // ISO date string like "2026-02-08"
  first_msg_time: string; // ISO timestamp
  last_msg_time: string; // ISO timestamp
  work_minutes: number;
  active_window_minutes: number;
  break_minutes: number;
  session_count: number;
  messages_sent: number;
}

export interface UseSetterDailyBreakdownOptions {
  organizationId: string | null;
  setterId: string | null;
  setterIds: string[]; // all setter IDs (RPC needs array)
  dateRange: { start: Date | null; end: Date | null };
  timezone?: string; // org timezone for date boundary conversion (default "UTC")
}

export function useSetterDailyBreakdown(options: UseSetterDailyBreakdownOptions) {
  const { organizationId, setterId, setterIds, dateRange, timezone } = options;

  // State
  const [data, setData] = useState<DailyBreakdownRow[]>([]);
  // EVERY setter's rows, not just the requested one. The RPC already returns
  // them all (get_setter_daily_breakdown gates on `= ANY(p_setter_ids)`) and the
  // filter below used to throw them away. The setter switcher inside
  // SessionMessagesModal reads these to know who else worked a given day —
  // zero extra network calls. `data` is unchanged for every existing caller.
  const [allRows, setAllRows] = useState<DailyBreakdownRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abort controller ref
  const abortRef = useRef<AbortController | null>(null);

  // Lazy fetch function (stale-while-revalidate)
  const fetch = useCallback(async () => {
    if (!organizationId || !setterId || !supabase || setterIds.length === 0) return;
    if (!dateRange.start || !dateRange.end) return;

    // Abort previous request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    // Stale-while-revalidate: show cached data instantly
    const cached = getDailyBreakdownCache<DailyBreakdownRow[]>(organizationId, dateRange, setterId);
    if (cached) {
      setData(cached);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc("get_setter_daily_breakdown", {
        p_organization_id: organizationId,
        p_setter_ids: setterIds,
        p_start_date: toOrgStartISO(dateRange.start, timezone),
        p_end_date: toOrgEndISO(dateRange.end, timezone),
      });

      if (rpcError) throw rpcError;

      // Parse numeric strings to numbers (Supabase returns NUMERIC as string)
      const parseNum = (v: unknown): number => (v != null ? parseFloat(v as string) : 0);

      // Chain split so the pre-filter array survives. Sorting BEFORE filtering
      // leaves `parsed` byte-identical to the old map→filter→sort order: the
      // sort key is work_date and the filter key is setter_id, so filtering a
      // sorted list preserves the same relative order it had before.
      const allParsed: DailyBreakdownRow[] = (rpcData || [])
        .map((row: Record<string, unknown>) => ({
          setter_id: row.setter_id as string,
          setter_name: row.setter_name as string,
          work_date: row.work_date as string,
          first_msg_time: row.first_msg_time as string,
          last_msg_time: row.last_msg_time as string,
          work_minutes: parseNum(row.work_minutes),
          active_window_minutes: parseNum(row.active_window_minutes),
          break_minutes: parseNum(row.break_minutes),
          session_count: Number(row.session_count ?? 0),
          messages_sent: Number(row.messages_sent ?? 0),
        }))
        .sort(
          (a: DailyBreakdownRow, b: DailyBreakdownRow) =>
            new Date(b.work_date).getTime() - new Date(a.work_date).getTime()
        ); // Sort by date DESC

      const parsed = allParsed.filter((row) => row.setter_id === setterId); // Filter to requested setter

      // The cache MUST stay the per-setter filtered array. Switching this to
      // `allParsed` makes every other setter's card read the wrong rows, with
      // no error and plausible-looking numbers.
      setDailyBreakdownCache(organizationId, dateRange, setterId, parsed);
      setData(parsed);
      setAllRows(allParsed);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (!cached) {
        console.warn("Failed to fetch daily breakdown:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch daily breakdown");
      }
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, setterId, setterIds, dateRange.start, dateRange.end]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return {
    data,
    allRows,
    isLoading,
    error,
    fetch,
  };
}
